import React, { useEffect, useState } from 'react'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useRouter } from 'expo-router'

import { Palette } from '@ppwr/poker'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'

import { OnboardingQuestion, OnboardingQuestionData } from './OnboardingQuestion'

// Question id 4 in apps/api/src/users/question-answers.ts — content is fetched from
// there via fetchOnboardingQuestions
const POKER_GOALS_QUESTION_ID = 4

export const PokerGoalsScreen = () => {
  const router = useRouter()
  const store = useStore()
  const [question, setQuestion] = useState<OnboardingQuestionData | null>(null)
  const [answers, setAnswers] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const loadQuestion = async () => {
      // onboarding/_layout.tsx fetches all onboarding questions once per onboarding
      // session; reuse that cache, falling back to a fetch if it isn't ready yet.
      const cached = store.session.onboardingQuestions?.questions
      const questions = cached ?? (await store.session.fetchOnboardingQuestions()).questions
      const found = questions?.find((q) => q.id === POKER_GOALS_QUESTION_ID)
      if (!found) {
        return
      }
      setQuestion({
        question: found.question ?? '',
        subtitle: found.subtitle ?? '',
        type: found.type === 'multiple' ? 'multiple' : 'single',
        values: found.values ? [...found.values] : [],
        footer: found.footer ?? undefined
      })
    }
    loadQuestion()
  }, [store.session])

  const trackPokerGoalsAnswer = (savedAnswers: string[]) => {
    store.analytics.setEvent(events.onboarding.reason_form_completed, {
      segmentUserId: store.analytics.getSegmentUserID(),
      segmentIdentity: store.analytics.getUserIdentify(),
      reason: savedAnswers.join(', ')
    })
  }

  const onPressNext = async () => {
    setIsSaving(true)
    try {
      await store.session.updateUserAnswers(POKER_GOALS_QUESTION_ID, answers)
    } catch (error) {
      console.error('Failed to save poker goals answer', error)
    } finally {
      setIsSaving(false)
    }
    trackPokerGoalsAnswer(answers)
    router.navigate('/onboarding/real-time-updates')
  }

  const onPressSkip = async () => {
    setIsSaving(true)
    try {
      await store.session.updateUserAnswers(POKER_GOALS_QUESTION_ID, [])
    } catch (error) {
      console.error('Failed to save poker goals skip', error)
    } finally {
      setIsSaving(false)
    }
    trackPokerGoalsAnswer([])
    router.navigate('/onboarding/real-time-updates')
  }

  return (
    <SafeAreaView style={{ backgroundColor: Palette['Brand/Cream'], flex: 1 }}>
      <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />
      <View style={{ flex: 1, paddingHorizontal: 24, paddingBottom: 16, gap: 20 }}>
        {question && <OnboardingQuestion question={question} answers={answers} onAnswersChange={setAnswers} />}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <DS.Button
            style={{ flex: 1 }}
            type="On Dark"
            variant="Medium/Secondary"
            disabled={!question || isSaving}
            onPress={onPressSkip}
          >
            Skip
          </DS.Button>
          <DS.Button
            style={{ flex: 1 }}
            type="On Light"
            variant="Medium/Primary"
            disabled={!question || answers.length === 0 || isSaving}
            onPress={onPressNext}
          >
            Next
          </DS.Button>
        </View>
      </View>
    </SafeAreaView>
  )
}
